import { TypedUseSelectorHook, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import type { CasinoSharedRootState } from '~/components/casino-shared/ducks/types';
import { EnhancedGame } from '~/components/casino-shared/types';

const REDUCER_NAME = 'liveCasino';

// [REDUCER_NAME]? , a question mark here is for search component
// if casino, or liveCasino, or allGames undefined in the store to not crash app
// some of them can be empty because we use explore page with categories, and without
// games without categories store in allGames reducer

export const useLiveCasinoSelector: TypedUseSelectorHook<CasinoSharedRootState> = useSelector;

export const campaignsSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.campaigns;
export const gamesByGameCodeSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.games;
export const hydratedSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.hydrated;
export const loadingSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.loading;
export const gameCodesByIdxSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.gameCodesByIdx;
export const gameNamesByIdxSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.gameNamesByIdx;
export const gameCodesByProviderCodeSelector = (state: CasinoSharedRootState) =>
	state[REDUCER_NAME].gameCodesByProviderCode;
export const gameCodesBySubProviderCodeSelector = (state: CasinoSharedRootState) =>
	state[REDUCER_NAME].gameCodesBySubProviderCode;
export const providersSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME].providers;
export const subProvidersSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME].subProviders;
export const liveCasinoDataSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.liveCasinoData;
export const carouselItemsSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME].carouselItems;
export const exploreCategoriesSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME].exploreCategories;
export const exploreLiveCasinoStatusSelector = (state: CasinoSharedRootState) =>
	state[REDUCER_NAME].exploreLiveCasinoStatus;
export const categoriesByCategoryIdSelector = (categoryId?: string) => (state: CasinoSharedRootState) =>
	state[REDUCER_NAME]?.exploreCategories.find(category => category.id === categoryId);
export const quickFiltersPositionSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME].quickFiltersPosition;

export const gamesByIndexSelector = createSelector(
	[gamesByGameCodeSelector, gameCodesByIdxSelector],
	(gamesByGameCode, gameCodes) => {
		return gameCodes?.reduce<Record<number, EnhancedGame>>((acc, gameCode, idx) => {
			acc[idx] = gamesByGameCode[gameCode];
			return acc;
		}, {});
	}
);

export const gamesByNameSelector = createSelector(
	[gameNamesByIdxSelector, gameCodesByIdxSelector, gamesByGameCodeSelector],
	(gameNames, gameCodes, gamesByGameCode) => {
		return gameCodes?.reduce<Record<string, EnhancedGame>>((acc, gameCode, idx) => {
			acc[gameNames[idx]] = gamesByGameCode[gameCode];
			return acc;
		}, {});
	}
);

export const gameLiveCasinoDataSelector = createSelector(
	liveCasinoDataSelector,
	(
		_: CasinoSharedRootState,
		game: {
			liveFeedCode?: EnhancedGame['liveFeedCode'];
			provider: EnhancedGame['provider'];
		} | null
	) => game,
	(liveCasinoData, game) => {
		const liveFeedCode = game?.liveFeedCode;

		if (!liveFeedCode) {
			return null;
		}

		const providerLiveData = liveCasinoData?.find(
			data => data.provider === game?.provider?.code.replace('_MOBILE', '')
		);

		return providerLiveData?.gameStates?.[liveFeedCode];
	}
);
